import Socket from '../connection/socket';
import * as MediaActions from '../stores/actions/media';
import { getState, socketListenerDispatch } from './socket-listener-main';
import type {
	MediaItem,
	PagedMediaItemExisting,
} from '../stores/types/media';
import { GreenroomState } from '../stores/types/types';
import { MEDIA_UPDATED_EVENTS } from '../types/constants';
import { FetchSingleMediaItem } from '../connection/media';
import { showAlert } from '../components/general-components/alert-service';
import { setSelectedMediaTemplate } from '../stores/actions/generate-content';

interface SocketMessage {
	type: string;
	message: unknown;
}

interface MediaPageUpdate extends SocketMessage {
	message: {
		mediaPage: PagedMediaItemExisting,
		from: string,
	};
}

interface MediaPagesDeleted extends SocketMessage {
	message: {
		mediaPageIds: number[],
		from: string,
	};
}

function handleSetMedia(mediaItem: MediaItem) {
	const {
		MediaReducer: {
			media,
			selectedMedia,
			selectedPage,
		},
	} = getState() as GreenroomState;

	const targetMedia = media.find((item: MediaItem) => item.id === mediaItem.id);

	if (targetMedia) {
		const newMedia = media.map((item: MediaItem) => {
			if (item.id === targetMedia.id) {
				return mediaItem;
			}
			return item;
		});

		socketListenerDispatch(MediaActions.updateMedia(newMedia));
	}

	// Update the media modal if user has it open right now
	if (selectedMedia?.id === mediaItem.id) {
		socketListenerDispatch(MediaActions.setSelectedMedia(mediaItem));

		// If user's selected page was the last page and creator deletes last page, select new last page
		const numPages = mediaItem?.images?.split(',').length ?? 0;
		if (numPages < selectedPage) {
			socketListenerDispatch(MediaActions.setSelectedPage(numPages));
		}
	}
}

function handleDeleteMediaItem(id: number) {
	const {
		MediaReducer: {
			selectedMedia,
		},
	} = getState() as GreenroomState;

	socketListenerDispatch(MediaActions.deleteMediaItem(id));

	if (selectedMedia?.id === id) {
		socketListenerDispatch(MediaActions.setMediaModalOpen(false, undefined));
	}
}

function handleMediaItemUpdate(mediaItem: MediaItem) {
	const {
		SessionReducer: {
			embed,
		},
		MediaReducer: {
			media,
			selectedMedia,
			selectedPage,
		},
	} = getState() as GreenroomState;

	// media was altered by another user - we want to update our state
	if (!embed) {
		if (media.some((item: MediaItem) => item.id === mediaItem.id)) {
			// generate new media object - don't mutate original
			const newMedia = media.map((item: MediaItem) => {
				if (item.id === mediaItem.id) {
					return mediaItem;
				}
				return item;
			});

			socketListenerDispatch(MediaActions.updateMedia(newMedia));

			// Update the media modal if user has it open right now
			if (selectedMedia?.id === mediaItem.id) {
				socketListenerDispatch(MediaActions.setSelectedMedia(mediaItem));

				// If user's selected page was the last page and creator deletes last page,
				// select new last page
				const numPages = mediaItem?.images?.split(',').length ?? 0;
				if (numPages < selectedPage) {
					socketListenerDispatch(MediaActions.setSelectedPage(numPages));
				}
			}
		} else {
			// update media with new array
			socketListenerDispatch(MediaActions.updateMedia([...media, mediaItem]));
		}
	}
}

function refreshMediaTab(message: any) {
	const {
		SessionReducer: {
			meeting,
			token,
			embed,
			user,
		},
	} = getState() as GreenroomState;

	if (token && meeting) {
		if (!embed && user && message.message?.event && message.message?.id) {
			socketListenerDispatch(MediaActions.setMediaUpdateEventInfo(
				{ mediaId: parseInt(message.message.id, 10), type: message.message?.event },
			));

			if (message.message.event === MEDIA_UPDATED_EVENTS.DOWNLOAD_READY) {
				socketListenerDispatch(MediaActions.setNotifyOnUpdate(true));
			}
		}

		socketListenerDispatch(MediaActions.getMedia(token, meeting.meeting));
	}
}

export async function updateSingleItem(message: any) {
	const {
		SessionReducer: {
			meeting,
			token,
		},
		GeneratedContentReducer: {
			selectedMediaItem,
		}
	} = getState() as GreenroomState;

	const mediaId = Number(message.message.id);
	if (token && meeting) {
		const mediaItem = await FetchSingleMediaItem(token, meeting.meeting, mediaId);
		if (mediaItem) {
			socketListenerDispatch(MediaActions.updateSingleMedia(mediaItem));
			if (selectedMediaItem?.id === mediaItem.id) {
				socketListenerDispatch(setSelectedMediaTemplate(mediaItem, mediaItem.template_type));
			}
		}
	}
}

function mediaUpdatedSingle({ message }: MediaPageUpdate) {
	const {
		SessionReducer: {
			session,
		},
	} = getState() as GreenroomState;

	if (message.from !== session) {
		socketListenerDispatch(MediaActions.updateMediaPage(message.mediaPage));
	}
}

function mediaPageIdsDeleted({ message }: MediaPagesDeleted) {
	socketListenerDispatch(MediaActions.deleteMediaPages(message.mediaPageIds));
}

function handleMediaConvertError(message: any) {
	const {
		SessionReducer: {
			token,
			meeting,
			user,
		},
		MediaReducer: {
			media,
		},
	} = getState() as GreenroomState;

	const mediaItem = media.find((item: MediaItem) => item.id === message.message);

	if (!mediaItem) return;

	// show error toaster if you uploaded this media
	if (user && (user.gr_user === mediaItem.created_by)) {
		showAlert({
			message: 'File Upload Error',
			description: 'The file selected could not be uploaded',
			duration: 4000,
			type: 'error',
		});
	}

	// remove item from media array if it is a new upload
	if (token && meeting?.meeting && !mediaItem.images) {
		socketListenerDispatch(MediaActions.deleteMediaItem(mediaItem.id));
	}
}

interface SocketPacketAutoplayAlert {
	message: {
		forUser: number;
		forShot: number;
	}
}

const handleAutoplayAlert = (socketPacket: SocketPacketAutoplayAlert) => {
	const {
		MeetingReducer: { scheduledStream },
		SessionReducer: { user },
		ShotsReducer: { currentShotId },
	} = getState() as GreenroomState;

	const startTimeNumber = scheduledStream?.startTime?.valueOf() ?? 0;

	if (startTimeNumber <= Date.now()) {
		return; // scheduled stream is in the past
	}

	const { forUser, forShot } = socketPacket.message;

	if (user?.gr_user === forUser && currentShotId === forShot) {
		showAlert({
			message: 'Please input a duration',
			description: 'The shot will skip during a scheduled stream',
			type: 'duration-notification',
			autoWidth: true,
		});
	}
};

export default function Media(socket: Socket) {
	socket.addListener('delete-media-item', handleDeleteMediaItem);
	socket.addListener('media-item-update', handleMediaItemUpdate);
	socket.addListener('set-media', handleSetMedia);
	socket.addListener('media-updated', refreshMediaTab);
	socket.addListener('media-updated-single', updateSingleItem);
	socket.addListener('trigger-trim-video-in-progress', refreshMediaTab);
	socket.addListener('trigger-trim-video-complete', refreshMediaTab);
	socket.addListener('media-page-updated-single', mediaUpdatedSingle);
	socket.addListener('media-page-ids-deleted', mediaPageIdsDeleted);
	socket.addListener('media-convert-error', handleMediaConvertError);

	socket.addListener('trigger-autoplay-alert', handleAutoplayAlert);
}
